import moment from 'moment';
import { useContext, useEffect, useState } from 'react';
import { WP_Event, WP_EventsArray } from '../model/models';
import { useLogger } from '../services/LogService';
import { ModelsService } from '../services/ModelsService';
import { AppContext } from '../state/AppContextProvider';
import { useWP } from './WPHooks';

/**
 * Hook useEventsList()
 * Fetch events and return it a local state
 */
export const useEventsList = () => {
  const { state } = useContext(AppContext);

  const { wp } = useWP();

  const [events, setEvents] = useState<WP_EventsArray>([]);

  const logger = useLogger('useEventsList');

  useEffect(() => {
    if (state.locale && state.user) {
      logger.info(
        `useEffect() [state.locale = '${state.locale}', state.user = ...)}]`,
        state.user
      );
      wp.getEventsList(state.locale).then((events) => {
        setEvents(ModelsService.sortEvents(events));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.locale, state.user]);

  return { events: events };
};

/**
 * Hook useEventsByHouse()
 * Fetch house events and return it a local state
 */
export const useEventsByHouse = () => {
  const { state } = useContext(AppContext);

  const { wp } = useWP();

  const [houseEvents, setHouseEvents] = useState<WP_EventsArray>([]);

  useEffect(() => {
    if (state.user && state.house && state.locale) {
      const houseId = state.house.term_id;
      if (houseEvents.length === 0) {
        // Get House News
        wp.getEventsByHouse(houseId, state.locale).then((houseEvents) => {
          setHouseEvents(ModelsService.sortEvents(houseEvents));
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.user, state.house, state.locale]);

  return { houseEvents: houseEvents };
};

export function useEventModal(event: WP_Event): {
  start_date: string;
  start_hour: string;
  end_date: string;
  end_hour: string;
  event_start_date_house: string;
  end_date_hour: string;
  eventHouseThumbnailUrl: string;
  eventHouseName: string;
  featuredImage: string;
  places: [];
} {
  const { state } = useContext(AppContext);

  const event_start_date = moment(event.acf.start);
  const start_hour =
    event_start_date.format('H') + 'h' + event_start_date.format('mm');
  const event_start_date_house = moment(event.acf.start).format('L');
  const start_date =
    event_start_date.format('D') + ' ' + event_start_date.format('MMM');

  let end_hour = '';
  let end_date = '';
  let end_date_hour = '';
  if (event.acf.end !== '') {
    const event_end_date = moment(event.acf.end);
    end_date_hour = moment(event.acf.end).format('L');
    end_hour = event_end_date.format('H') + 'h' + event_start_date.format('mm');
    end_date = event_end_date.format('D') + ' ' + event_end_date.format('MMM');
  }

  const featuredImage =
    event.fimg_url !== false ? (event.fimg_url as string) : '';

  const eventHouse = ModelsService.getHouseTerm(
    event.acf.organizer,
    state.allHouses
  );

  const eventService = event.acf.service
    ? ModelsService.getServiceTerm(event.acf.service.term_id, state.allServices)
    : undefined;

  const eventHouseThumbnailUrl = event.acf.service
    ? ModelsService.getServiceThumbnail(eventService)
    : ModelsService.getHouseThumbnail(eventHouse);
  const eventHouseName = event.acf.service
    ? ModelsService.getServiceNameFromTerm(eventService, state.user?.acf.locale)
    : ModelsService.getHouseNameFromTerm(eventHouse);

  const places = event.acf.locations;

  return {
    start_date,
    start_hour,
    end_date,
    end_hour,
    event_start_date_house,
    end_date_hour,
    eventHouseThumbnailUrl,
    eventHouseName,
    featuredImage,
    places,
  };
}
